import React, { useEffect, useState, useRef } from 'react';
import {
  getWorkers, createWorker, updateWorker, deleteWorker, reorderWorkers,
  getShops, getFactories,
} from '../../services/api';
import toast from 'react-hot-toast';

const dragHandleStyle = { cursor: 'grab', fontSize: 16, userSelect: 'none' };

export default function Workers() {
  const [items, setItems] = useState([]);
  const [shops, setShops] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', shops: [], factories: [] });
  const [saving, setSaving] = useState(false);

  const [draggingId, setDraggingId] = useState(null);
  const dragItem = useRef(null);
  const dragBackup = useRef(null);
  const lastTarget = useRef(null);
  const dropDone = useRef(false);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([
      getWorkers(showInactive),
      getShops(false),
      getFactories(false),
    ])
      .then(([wRes, sRes, fRes]) => {
        setItems(wRes.data);
        setShops(sRes.data);
        setFactories(fRes.data);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Σφάλμα'))
      .finally(() => { if (!silent) setLoading(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', shops: [], factories: [] });
    setShowModal(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({
      name: w.name,
      shops: w.shops || [],
      factories: w.factories || [],
    });
    setShowModal(true);
  };

  const toggleShop = (id) => {
    setForm((f) => ({
      ...f,
      shops: f.shops.includes(id) ? f.shops.filter((x) => x !== id) : [...f.shops, id],
    }));
  };
  const toggleFactory = (id) => {
    setForm((f) => ({
      ...f,
      factories: f.factories.includes(id) ? f.factories.filter((x) => x !== id) : [...f.factories, id],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Συμπληρώστε όνομα');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        shops: form.shops,
        factories: form.factories,
      };
      if (editing) {
        await updateWorker(editing.id, payload);
        toast.success('Ενημερώθηκε');
      } else {
        await createWorker(payload);
        toast.success('Δημιουργήθηκε');
      }
      setShowModal(false);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async (w) => {
    if (!window.confirm(`Απενεργοποίηση: ${w.name};`)) return;
    try { await deleteWorker(w.id, false); toast.success('Απενεργοποιήθηκε'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const reactivate = async (w) => {
    try { await updateWorker(w.id, { is_active: true }); toast.success('Ενεργοποιήθηκε'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };
  const hardDelete = async (w) => {
    if (!window.confirm(`ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ: ${w.name};\nΑυτή η ενέργεια δεν αναιρείται.`)) return;
    try { await deleteWorker(w.id, true); toast.success('Διαγράφηκε οριστικά'); load(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  // Drag handlers — but only for non-unknown workers
  const onDragStart = (e, w) => {
    if (w.is_unknown) { e.preventDefault(); return; }
    dragItem.current = w; dragBackup.current = null;
    lastTarget.current = null; dropDone.current = false;
    setDraggingId(w.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', '');
  };
  const onDragOver = (e, target) => {
    e.preventDefault();
    if (!dragItem.current || dragItem.current.id === target.id || target.is_unknown) return;
    if (lastTarget.current === target.id) return;
    setItems((prev) => {
      if (!dragBackup.current) dragBackup.current = [...prev];
      const fi = prev.findIndex((x) => x.id === dragItem.current.id);
      const ti = prev.findIndex((x) => x.id === target.id);
      if (fi === -1 || ti === -1 || fi === ti) return prev;
      const n = [...prev]; n.splice(fi, 1); n.splice(ti, 0, dragItem.current);
      lastTarget.current = target.id;
      return n;
    });
  };
  const onDrop = (e) => { e.preventDefault(); dropDone.current = true; };
  const onDragEnd = async () => {
    const wasDrop = dropDone.current; const backup = dragBackup.current;
    setDraggingId(null); dragItem.current = null;
    lastTarget.current = null; dropDone.current = false;
    if (!wasDrop) { if (backup) setItems(backup); dragBackup.current = null; return; }
    dragBackup.current = null;
    try {
      // Only non-unknown ids
      await reorderWorkers(items.filter((x) => !x.is_unknown).map((x) => x.id));
    } catch {
      toast.error('Σφάλμα σειράς');
      if (backup) setItems(backup);
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div><h2>🧑‍🔧 Εργαζόμενοι</h2><p>Άτομα που εργάζονται σε καταστήματα και εργοστάσια</p></div>
          <div className="flex-row">
            <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Ανενεργά
            </label>
            <button className="btn btn-primary" onClick={openCreate}>+ Νέος Εργαζόμενος</button>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="page-loading"><span className="loading-spinner dark" /></div>
            ) : items.length === 0 ? (
              <div className="empty-state"><div>🧑‍🔧</div><p>Δεν υπάρχουν εργαζόμενοι</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.map((w) => {
                  const isDragging = draggingId === w.id;
                  const shopNames = (w.shops || []).map((sid) => shops.find((s) => s.id === sid)?.name).filter(Boolean);
                  const factoryNames = (w.factories || []).map((fid) => factories.find((f) => f.id === fid)?.name).filter(Boolean);
                  return (
                    <div key={w.id}
                      onDragOver={(e) => onDragOver(e, w)} onDrop={onDrop}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                        border: '1px solid var(--gray-100)', borderRadius: 8,
                        background: isDragging ? '#dcfce7' : (w.is_unknown ? '#f5f3ff' : (w.is_active ? 'white' : 'var(--gray-50)')),
                        opacity: isDragging ? 0.5 : (w.is_active ? 1 : 0.6),
                      }}>
                      <span draggable={!w.is_unknown} onDragStart={(e) => onDragStart(e, w)} onDragEnd={onDragEnd}
                        style={{ ...dragHandleStyle, color: w.is_unknown ? 'var(--gray-200)' : (isDragging ? '#22c55e' : 'var(--gray-300)') }}>⠿</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, color: 'var(--gray-800)' }}>
                          {w.name}
                          {w.is_unknown && <span style={{ color: '#7c3aed', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>(ειδικός)</span>}
                          {!w.is_active && <span style={{ color: 'var(--gray-400)', fontWeight: 400, marginLeft: 6 }}>(ανενεργός)</span>}
                        </div>
                        {!w.is_unknown && (
                          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                            {shopNames.length > 0 && <>🏪 {shopNames.join(', ')}</>}
                            {shopNames.length > 0 && factoryNames.length > 0 && <span> · </span>}
                            {factoryNames.length > 0 && <>🏭 {factoryNames.join(', ')}</>}
                            {shopNames.length === 0 && factoryNames.length === 0 && <span style={{ color: 'var(--red)' }}>χωρίς ανάθεση</span>}
                          </div>
                        )}
                        {w.is_unknown && (
                          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                            Διαθέσιμος σε όλα τα καταστήματα και εργοστάσια
                          </div>
                        )}
                      </div>
                      <div className="flex-row">
                        {!w.is_unknown && w.is_active && (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(w)}>✏️</button>
                            <button className="btn btn-danger btn-sm" onClick={() => softDelete(w)}>🗑</button>
                          </>
                        )}
                        {!w.is_unknown && !w.is_active && (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => reactivate(w)} title="Επαναφορά">↻</button>
                            <button className="btn btn-danger btn-sm" onClick={() => hardDelete(w)} title="Οριστική Διαγραφή">❌</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontWeight: 600 }}>{editing ? 'Επεξεργασία Εργαζομένου' : 'Νέος Εργαζόμενος'}</span>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <div className="form-group">
                <label className="form-label">Όνομα *</label>
                <input className="form-input" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Καταστήματα</label>
                {shops.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Δεν υπάρχουν καταστήματα.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10 }}>
                    {shops.map((s) => (
                      <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                        <input type="checkbox" checked={form.shops.includes(s.id)} onChange={() => toggleShop(s.id)} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Εργοστάσια</label>
                {factories.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Δεν υπάρχουν εργοστάσια.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10 }}>
                    {factories.map((f) => (
                      <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                        <input type="checkbox" checked={form.factories.includes(f.id)} onChange={() => toggleFactory(f.id)} />
                        {f.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Άκυρο</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : 'Αποθήκευση'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
